import { RECAPTCHA_SITE_KEY } from "appConfiguration";
import Script from "next/script";
import { createContext, type ReactNode, useContext, useState } from "react";

declare global {
  interface Window {
    grecaptcha?: {
      execute: (
        siteKey: string,
        { action }: { action: string }
      ) => Promise<string>;
      ready: (callback: () => void) => void;
    };
  }
}

export type GreCaptchaContextType =
  | {
      isLoading: true;
      execute: null;
    }
  | { isLoading: false; execute: (action: string) => Promise<string> };

const GreCaptchaContext = createContext<GreCaptchaContextType>({
  isLoading: true,
  execute: null,
});

async function execute(action: string) {
  if (!window.grecaptcha) {
    throw new Error("grecaptcha not found");
  }
  const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
    action,
  });
  return token;
}

export function useGReCaptcha() {
  const context = useContext(GreCaptchaContext);
  if (!context) {
    throw new Error("useGReCaptcha must be used within a GreCaptchaProvider");
  }
  return context;
}
const recaptchaURL = new URL("/recaptcha/api.js", "https://www.google.com");
recaptchaURL.searchParams.set("render", RECAPTCHA_SITE_KEY);
const recaptchaSrc = recaptchaURL.toString();

export function GreCaptchaProvider({ children }: { children: ReactNode }) {
  const [providerValue, setProviderValue] = useState<GreCaptchaContextType>(
    () => {
      if (!window.grecaptcha) {
        return {
          isLoading: true,
          execute: null,
        };
      }
      return {
        isLoading: false,
        execute,
      };
    }
  );

  return (
    <>
      <Script
        src={recaptchaSrc}
        onLoad={() => {
          if (!window.grecaptcha) {
            return;
          }
          window.grecaptcha.ready(() => {
            setProviderValue({
              isLoading: false,
              execute,
            });
          });
        }}
      />
      <GreCaptchaContext.Provider value={providerValue}>
        {children}
      </GreCaptchaContext.Provider>
    </>
  );
}
